import AddIcon from '@mui/icons-material/Add';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
    useMediaQuery,
    useTheme
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useSnackbar } from 'notistack';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Prompt, useHistory, useParams } from 'react-router-dom';
import DeleteIconButton from '../../components/DeleteIconButton';

import { Tab, Tabs } from '@mui/material';
import { CREATE, DataAPIContext, GET_ONE, UPDATE } from '../../api';
import FixedLinearProgress from '../../components/FixedLinearProgress';
import FormActionButtons from '../../components/FormActionButtons';
import FormLicenseeContext, { toLicenseeId } from '../../components/FormLicenseeContext';
import LicenseeFormField from '../../components/LicenseeFormField';
import NavigateBackBar from '../../components/NavigateBackBar';
import PageMeta from '../../components/PageMeta';
import QuillEditor from '../../components/QuillEditor';
import SystemTagsDropdown from '../../components/SystemTagsDropdown';
import TabPanel, { a11yProps } from '../../components/TabPanel';
import UsedByPanel from '../../components/UsedByPanel';
import { ContextStore } from '../../store/contextStore';
import { LANGUAGE_MAP, LANGUAGES } from '../../utils/lang';

const useStyles = makeStyles((theme) => ({
    root: {
        margin: theme.spacing(2),
        marginTop: '4px',
        overflow: 'auto',
        backgroundColor: '#FCFCFE',
        position: 'relative',
        minHeight: `calc(100vh - 4rem)`,
        [theme.breakpoints.down('sm')]: {
            margin: 0,
        },
    },
    container: {
        backgroundColor: '#FCFCFE',
        minHeight: '100vh',
        paddingBottom: '120px'
    },
    mainForm: {
        padding: theme.spacing(2.5),
        borderRadius: '16px',
        border: '1px solid #D8D8D8',
        backgroundColor: '#FFFFFF',
    },
    sectionTitle: {
        fontSize: '20px',
        fontWeight: '500 !important',
        color: '#202020',
        paddingBottom: theme.spacing(1),
    },
    description: {
        fontSize: '13px',
        color: '#202020',
        paddingBottom: theme.spacing(3),
        maxWidth: '659px'
    },
    languageSection: {
        border: '1px solid #D8D8D8',
        borderRadius: '16px',
        padding: theme.spacing(2),
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2),
        '& .MuiBox-root': {
            '& .MuiBox-root': { // quill container
                paddingBottom: 0,
                marginBottom: 0,
                '& .ql-toolbar': {
                    border: '1px solid #E8E8E8',
                    borderBottom: 'none'
                },
                '& .ql-container': {
                    border: 'none'
                }
            }
        }
    },
    languageHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        '& .MuiGrid-root': {
            marginBottom: 0
        },
        '& .MuiIconButton-root': {
            [theme.breakpoints.down('sm')]: {
                padding: 0,
                paddingTop: '16px'
            },
        }
    },
    languageHeaderControls: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        paddingTop: '16px'
    },
    collapseButton: {
        color: '#007AFF',
        border: '1px solid #007AFF',
        borderRadius: '8px',
        padding: '8px',
        minWidth: 'auto',
        '&:hover': {
            backgroundColor: 'rgba(0, 122, 255, 0.1)'
        }
    },
    quillContainer: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2),
        '& .ql-toolbar': {
            backgroundColor: '#FFFFFF',
            borderTop: '1px solid #D8D8D8',
            borderLeft: '1px solid #D8D8D8',
            borderRight: '1px solid #D8D8D8',
            borderRadius: '8px 8px 0 0'
        },
        '& .ql-container': {
            backgroundColor: '#F5F5F5',
            border: '1px solid #747475',
            borderRadius: '0 0 8px 8px',
            minHeight: '300px'
        }
    },
    addLanguageButton: {
        color: '#007AFF',
        borderColor: '#007AFF',
        textTransform: 'uppercase',
        fontSize: '14px',
        fontWeight: 500,
        letterSpacing: '7.14%',
        marginTop: theme.spacing(2),
        [theme.breakpoints.down('sm')]: {
            width: '100%',
        },
    },
    bottomBar: {
        position: 'sticky',
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: '#F5F5F5',
        padding: theme.spacing(1, 3),
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        zIndex: 1000,
    },
    removeButton: {
        color: '#FE3F35',
        borderColor: '#FE3F35',
        textTransform: 'none',
        fontSize: '14px'
    }
}));

// A waiver has one name and one short description; only its text is per language (CP-8518).
const defaultLanguageBlock = {
    language: 'en',
    content: ''
};

// An emptied editor still holds markup such as <p><br></p>, so a text counts as written only when
// something is left once the tags are gone (an image counts).
const isBlankHtml = (html) =>
    !html || html.replace(/<(?!img\b)[^>]*>/gi, '').replace(/&nbsp;/gi, ' ').trim() === '';

// What still keeps the waiver from saving, or null. SAVE stays off while there is something: a
// language version without its text used to be left out of the save without a word (CP-8518).
const findSaveProblem = (waiver, isFranchise) => {
    if (isFranchise && !waiver.tour_operator) {
        return 'Please select a licensee';
    }
    if (!(waiver.name || '').trim()) {
        return 'Waiver name is required';
    }
    if (waiver.languages_data.some((block) => !block.language)) {
        return 'Select a language for every language version';
    }
    if (waiver.languages_data.some((block) => isBlankHtml(block.content))) {
        return 'Add the waiver text for every language version';
    }
    return null;
};

const languageBlockHint = (block, index) => {
    if (!block.language) {
        return 'Select a language for this version, then add its waiver text.';
    }
    if (isBlankHtml(block.content)) {
        const languageName = LANGUAGE_MAP[block.language]?.name || block.language;
        return index > 0
            ? `Add the waiver text in ${languageName}, or delete this language version.`
            : `Add the waiver text in ${languageName}.`;
    }
    return null;
};

const WaiverEdit = () => {
    const classes = useStyles();
    const theme = useTheme();
    const isMediumUp = useMediaQuery(theme.breakpoints.up('md'));
    const { id } = useParams();
    const history = useHistory();
    const dataAPI = React.useContext(DataAPIContext);
    const { enqueueSnackbar } = useSnackbar();
    const { hasPerm, isFranchise, currentLicensee } = React.useContext(ContextStore);
    const hasWritePerm = hasPerm('waiver', 'write');

    const [waiver, setWaiver] = useState({
        name: '',
        description: '',
        text: {
            values: {}
        },
        tour_operator: currentLicensee || null,
        languages_data: [{ ...defaultLanguageBlock }]
    });

    const onLicenseeChange = (licenseeId) => {
        setWaiver((prev) => ({ ...prev, tour_operator: licenseeId }));
        setIsDirty(true);
    };

    const [loading, setLoading] = useState(id ? true : false);
    const [saving, setSaving] = useState(false);
    const [expandedSection, setExpandedSection] = useState(0);
    const [isDirty, setIsDirty] = useState(false);
    const [currentTab, setCurrentTab] = useState(0);
    const [variablesTree, setVariablesTree] = useState(null);

    // The rich-text editor rewrites loaded HTML into its own markup and reports that once, ~300 ms
    // after it mounts. Counting that as an edit lit SAVE and the leave prompt on an untouched waiver
    // (CP-8423), so a block's content only marks the form dirty once the user has used its editor.
    const editorTouchedRef = useRef({});
    const markEditorTouched = (index) => {
        editorTouchedRef.current[index] = true;
    };

    const loadWaiver = useCallback(async () => {
        if (!hasWritePerm) {
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            const res = await dataAPI(GET_ONE, 'waiver', { id });

            if (res && res.data) {
                // Convert LocalizedTextSetField format to languages_data format
                const languagesData = Object.entries(res.data.text?.values || {}).map(([langCode, content]) => ({
                    language: langCode,
                    content: content,
                }));
                if (languagesData.length === 0) {
                    languagesData.push({ ...defaultLanguageBlock });
                }

                const { category_tags, ...restData } = res.data;
                setWaiver({
                    ...restData,
                    name: res.data.name || '',
                    description: res.data.description || '',
                    tags: category_tags || [],
                    languages_data: languagesData
                });
                // A freshly loaded editor's rewrite is not an edit, even where the user typed
                // before (a new waiver reloads here once it is created).
                editorTouchedRef.current = {};
                setIsDirty(false);
            }
        } catch (error) {
            enqueueSnackbar('Error loading waiver data', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    }, [id, dataAPI, enqueueSnackbar, hasWritePerm]);

    useEffect(() => {
        if (!hasWritePerm) {
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const tvRes = await dataAPI(GET_ONE, 'waiver', { urlSuffix: 'template_variables/' });
                if (cancelled) {
                    return;
                }
                const vt = tvRes?.data?.waivers_variables;
                setVariablesTree(vt && typeof vt === 'object' && Object.keys(vt).length > 0 ? vt : null);
            } catch {
                if (!cancelled) {
                    setVariablesTree(null);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [dataAPI, hasWritePerm]);

    useEffect(() => {
        if (!hasWritePerm) {
            setLoading(false);
            return;
        }

        if (id) {
            loadWaiver();
        } else {
            // Set default name for new waiver in first language block
            setWaiver(prev => ({
                ...prev,
                languages_data: [{ ...defaultLanguageBlock }]
            }));
        }
    }, [id, loadWaiver, hasWritePerm]);


    const handleChange = (e) => {
        const { name, value, checked, type } = e.target;
        const val = type === 'checkbox' ? checked : value;
        setWaiver(prev => ({ ...prev, [name]: val }));
        setIsDirty(true);
    };

    const handleWaiverTagsChange = useCallback((e, tags) => {
        if (tags !== undefined) {
            setWaiver(prev => ({
                ...prev,
                tags: tags || []
            }));
            setIsDirty(true);
        }
    }, []);

    const handleLanguageBlockChange = (index, field, value) => {
        if (!hasWritePerm) return;

        const updatedLanguagesData = [...waiver.languages_data];
        updatedLanguagesData[index] = {
            ...updatedLanguagesData[index],
            [field]: value
        };
        setWaiver(prev => ({ ...prev, languages_data: updatedLanguagesData }));
        if (field === 'content' && !editorTouchedRef.current[index]) return;
        setIsDirty(true);
    };

    const handleAddLanguage = () => {
        const newIndex = waiver.languages_data.length;
        setWaiver(prev => ({
            ...prev,
            languages_data: [...prev.languages_data, { ...defaultLanguageBlock, language: '' }]
        }));
        // Automatically expand the new language block
        setExpandedSection(newIndex);
        setIsDirty(true);
    };

    const handleRemoveLanguage = (index) => {
        if (waiver.languages_data.length === 1) {
            enqueueSnackbar('At least one language is required', { variant: 'warning' });
            return;
        }

        const updatedLanguagesData = waiver.languages_data.filter((_, i) => i !== index);
        setWaiver(prev => ({ ...prev, languages_data: updatedLanguagesData }));
        setIsDirty(true);

        // Adjust expanded section index
        setExpandedSection(prev => {
            if (prev === index) {
                // If removing the expanded section, expand the first one
                return 0;
            } else if (prev > index) {
                // If expanded section is after the removed one, decrease index
                return prev - 1;
            }
            // If expanded section is before the removed one, keep it unchanged
            return prev;
        });
    };

    const handleToggleCollapse = (index) => {
        setExpandedSection(prev => {
            if (prev === index) {
                return -1;
            }
            return index;
        });
    };

    const handleSave = async () => {
        if (!hasWritePerm) {
            enqueueSnackbar('You do not have permission to perform this action', { variant: 'error' });
            return;
        }
        const saveProblem = findSaveProblem(waiver, isFranchise);
        if (saveProblem) {
            enqueueSnackbar(saveProblem, { variant: 'error' });
            return;
        }

        // Check for duplicate languages
        const selectedLanguages = waiver.languages_data
            .filter(lang => lang.language)
            .map(lang => lang.language);
        const uniqueLanguages = [...new Set(selectedLanguages)];

        if (selectedLanguages.length !== uniqueLanguages.length) {
            enqueueSnackbar('Duplicate languages are not allowed', { variant: 'error' });
            return;
        }

        try {
            setSaving(true);

            // Convert languages_data back to LocalizedTextSetField format
            const textValues = {};
            waiver.languages_data.forEach(lang => {
                textValues[lang.language] = lang.content;
            });

            const { tags, ...restWaiver } = waiver;
            const dataToSave = {
                ...restWaiver,
                name: waiver.name.trim(),
                description: waiver.description,
                text: { values: textValues },
                category_tags: tags,
                tour_operator: toLicenseeId(waiver.tour_operator),
                // Remove languages_data as it's not part of the backend model
                languages_data: undefined
            };

            const action = id ? UPDATE : CREATE;
            const params = id ? { id, data: dataToSave } : { data: dataToSave };

            const response = await dataAPI(action, 'waiver', params);
            if (!response || response.statusCode >= 400) {
                const backendMessage = response?.data?.message || response?.data?.detail;
                enqueueSnackbar(backendMessage || 'You do not have permission to perform this action', { variant: 'error' });
                return;
            }

            enqueueSnackbar(id ? 'Waiver updated successfully' : 'Waiver created successfully', { variant: 'success' });
            setIsDirty(false);
            // Stay on the waiver after a save; a new one opens as itself (CP-8518).
            if (!id) {
                history.replace(response.data?.id ? `/waivers/${response.data.id}` : '/waivers');
            }
        } catch (error) {
            enqueueSnackbar('Error saving waiver', { variant: 'error' });
        } finally {
            setSaving(false);
        }
    };

    if (!hasWritePerm) {
        return (
            <>
                <PageMeta title={id ? 'Edit Waiver' : 'Create New Waiver'} />
                <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" height="100vh" gap={2}>
                    <Typography>You do not have permission to edit waivers.</Typography>
                    <Button variant="contained" color="primary" onClick={() => history.push('/waivers')}>
                        Go back to waivers
                    </Button>
                </Box>
            </>
        );
    }

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <>
            <PageMeta title={id ? 'Edit Waiver' : 'Create New Waiver'} />
            <Prompt
                when={isDirty && !saving}
                message="Your changes will be lost. Are you sure you want to leave this page?"
            />

            <FormLicenseeContext.Provider value={toLicenseeId(waiver.tour_operator)}>
            <Box className={classes.container}>
                <div className={classes.root}>
                    <FixedLinearProgress visibility={saving} />
                    <NavigateBackBar
                        backButtonNavbarStyle={{
                            marginLeft: isMediumUp ? '-6px' : undefined,
                            paddingLeft: isMediumUp ? '0px' : '10px'
                        }}
                        label={id ? 'Edit Waiver' : 'Create New Waiver'}
                        url="/waivers"
                        labelStyle={{
                            fontWeight: 500,
                            fontSize: '16px'
                        }}
                    />

                    {id && (
                        <Tabs value={currentTab} onChange={(e, v) => setCurrentTab(v)} indicatorColor="primary" textColor="primary" sx={{ mb: 1 }}>
                            <Tab label="General" {...a11yProps(0)} />
                            <Tab label="Used by" {...a11yProps(1)} />
                        </Tabs>
                    )}
                    <TabPanel value={currentTab} index={0}>
                    {/* Main Form */}
                    <Box className={classes.mainForm}>
                        <Typography className={classes.sectionTitle}>
                            Waiver Settings
                        </Typography>
                        <Typography variant="body2" className={classes.description}>
                            Write waiver content and add multiple languages.
                        </Typography>

                        {/* Basic Fields */}
                        <Box sx={{
                            flex: 1,
                            width: '100%',
                            [theme.breakpoints.down('sm')]: {
                                mx: 0,
                            }
                        }}>
                            <Box mb={2}>
                                <LicenseeFormField
                                    value={waiver.tour_operator}
                                    onChange={onLicenseeChange}
                                    disabled={!hasWritePerm}
                                    error={isFranchise && !waiver.tour_operator}
                                    helperText={isFranchise && !waiver.tour_operator ? 'Licensee is required' : undefined}
                                />
                            </Box>
                            <TextField
                                label="Waiver name"
                                name="name"
                                value={waiver.name}
                                onChange={handleChange}
                                variant="filled"
                                fullWidth
                                disabled={!hasWritePerm}
                                required
                                error={isDirty && !waiver.name.trim()}
                                helperText={isDirty && !waiver.name.trim() ? 'Waiver name is required' : undefined}
                                sx={{ mb: 2 }}
                            />
                            <TextField
                                label="Short waiver description"
                                name="description"
                                value={waiver.description}
                                onChange={handleChange}
                                variant="filled"
                                fullWidth
                                disabled={!hasWritePerm}
                                sx={{ mb: 2 }}
                            />
                            <SystemTagsDropdown
                                value={waiver.tags || []}
                                onChange={(e, tags) => {
                                    handleWaiverTagsChange(e, tags);
                                }}
                                disabled={!hasWritePerm}
                                label="Show only to guests with following tags"
                                variant="filled"
                                size="small"
                                renderTags={(value, getTagProps) =>
                                    value.map((option, index) => (
                                        <Chip
                                            key={option.id}
                                            variant="outlined"
                                            label={`${option.category.name}: ${option.name}`}
                                            size="small"
                                            {...getTagProps({ index })}
                                            sx={{ height: '24px', fontSize: '12px' }}
                                        />
                                    ))
                                } />
                        </Box>

                        {/* Language Sections */}
                        {waiver.languages_data.map((langData, index) => {
                            const isExpanded = expandedSection === index;
                            const hint = hasWritePerm && languageBlockHint(langData, index);
                            return (
                                <Box key={index} className={classes.languageSection}>
                                    <Box className={classes.languageHeader}>
                                        <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
                                            <Grid item xs={12} sm={4} sx={{ pr: { xs: 0, sm: 0 }, pl: { xs: 0, sm: 0 } }}>
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    <FormControl sx={{ flexGrow: 1, minWidth: 0, width: { xs: 'calc(100% - 60px)', sm: '100%' } }} variant="filled" disabled={!hasWritePerm}>
                                                        <InputLabel>Select waiver language</InputLabel>
                                                        <Select
                                                            value={langData.language}
                                                            onChange={(e) => handleLanguageBlockChange(index, 'language', e.target.value)}
                                                        >
                                                            {LANGUAGES.map(lang => (
                                                                <MenuItem
                                                                    key={lang.code}
                                                                    value={lang.code}
                                                                    disabled={waiver.languages_data.some(
                                                                        (l, i) => i !== index && l.language === lang.code
                                                                    )}
                                                                >
                                                                    {lang.name}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                    <Box sx={{ display: { xs: 'flex', sm: 'none' }, flexShrink: 0, gap: 1, width: '60px', justifyContent: 'flex-end' }}>
                                                        {index > 0 && (
                                                            <DeleteIconButton
                                                                title="Delete language"
                                                                allowed={hasWritePerm}
                                                                onClick={() => handleRemoveLanguage(index)}
                                                            />
                                                        )}
                                                        {langData.language && (
                                                            <IconButton
                                                                onClick={() => handleToggleCollapse(index)}
                                                                className={classes.collapseButton}
                                                                size="small"
                                                                title={isExpanded ? "Collapse section" : "Expand section"}
                                                            >
                                                                {isExpanded ? <ExpandLessIcon style={{ color: '#007AFF' }} /> : <ExpandMoreIcon style={{ color: '#007AFF' }} />}
                                                            </IconButton>
                                                        )}
                                                    </Box>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={0} sm={8} sx={{ display: { xs: 'none', sm: 'flex' }, justifyContent: 'flex-end' }}>
                                                <Box className={classes.languageHeaderControls}>
                                                    {/* Remove button */}
                                                    {index > 0 && (
                                                        <DeleteIconButton
                                                            title="Delete language"
                                                            allowed={hasWritePerm}
                                                            onClick={() => handleRemoveLanguage(index)}
                                                        />
                                                    )}

                                                    {/* Collapse/Expand button */}
                                                    {langData.language && (
                                                        <IconButton
                                                            onClick={() => handleToggleCollapse(index)}
                                                            className={classes.collapseButton}
                                                            size="small"
                                                            title={isExpanded ? "Collapse section" : "Expand section"}
                                                        >
                                                            {isExpanded ? <ExpandLessIcon style={{ color: '#007AFF' }} /> : <ExpandMoreIcon style={{ color: '#007AFF' }} />}
                                                        </IconButton>
                                                    )}
                                                </Box>
                                            </Grid>
                                        </Grid>
                                    </Box>

                                    {hint && (
                                        <Typography variant="body2" color="text.secondary">
                                            {hint}
                                        </Typography>
                                    )}

                                    {/* Content Section - conditionally visible when not collapsed and language is selected */}
                                    {langData.language && isExpanded && (
                                        <Box sx={{ mt: 2 }}>
                                            {/* QuillEditor */}
                                            <Box
                                                className={classes.quillContainer}
                                                onKeyDownCapture={() => markEditorTouched(index)}
                                                onMouseDownCapture={() => markEditorTouched(index)}
                                                onPasteCapture={() => markEditorTouched(index)}
                                                onDropCapture={() => markEditorTouched(index)}
                                            >
                                                <QuillEditor
                                                    value={langData.content}
                                                    onChange={(content) => handleLanguageBlockChange(index, 'content', content)}
                                                    containerHeight="300px"
                                                    variablesTree={variablesTree}
                                                />
                                            </Box>
                                        </Box>
                                    )}
                                </Box>
                            );
                        })}

                        {/* Add Language Button */}
                        {hasWritePerm && (
                            <Button
                                variant="outlined"
                                className={classes.addLanguageButton}
                                onClick={handleAddLanguage}
                                startIcon={<AddIcon />}
                            >
                                Add Language Version
                            </Button>
                        )}
                    </Box>
                    </TabPanel>
                    {id && (
                        <TabPanel value={currentTab} index={1}>
                            <UsedByPanel resource="waiver" id={id} />
                        </TabPanel>
                    )}
                </div>
            </Box>

            {/* Bottom Bar */}
            <Box className={classes.bottomBar}>
                <Box display="flex" alignItems="center" justifyContent="flex-end" flex={1}>
                    {saving && <CircularProgress size={20} sx={{ mr: 1 }} />}
                    <FormActionButtons
                        mode={id ? 'edit' : 'create'}
                        onSubmit={handleSave}
                        submitDisabled={saving || !hasWritePerm || !isDirty || Boolean(findSaveProblem(waiver, isFranchise))}
                        onCancel={() => history.push('/waivers')}
                        size="medium"
                    />
                </Box>
            </Box>
            </FormLicenseeContext.Provider>
        </>
    );
};

export default WaiverEdit; 